import type { QueryClient } from '@tanstack/react-query'
import { getSession, subscribe, type Session } from '../auth/session'

// v030-eng-perf-001 — « cache invalidation does not serve stale scope ».
//
// MESURÉ, PAS SUPPOSÉ : `logout()` (auth/session.ts) ne fait NI `window.location.reload()`
// NI `queryClient.clear()` — une pure mise à jour d'état SPA. Le `QueryClient` unique créé
// dans `main.tsx` PERSISTE donc across logout/login dans le même onglet. Plusieurs écrans
// (`Dashboard.tsx` : `['workspaces']`, `['portfolio']`, `['decisions']`, `['models']`,
// `['agents']` — AUCUN scopé par organisation/utilisateur) resteraient en cache jusqu'à
// `staleTime` (30s, `main.tsx`) après un changement d'identité : le même défaut de classe
// que celui déjà corrigé sur `useNavigationMode` (clé invariante, donnée dépendante de
// l'identité), mais qui ne peut PAS se corriger clé par clé sans risquer d'en oublier une
// (23 candidats mesurés par `verifier_cache_scope_react_query.py`, dont plusieurs légitimes).
//
// Le correctif structurel : vider ENTIÈREMENT le cache dès que l'IDENTITÉ change (pas à
// chaque mise à jour de session — un rafraîchissement de jeton pour la MÊME identité ne
// doit jamais coûter un cache vidé). `identity()` reprend exactement le calcul déjà
// éprouvé de `useNavigationMode` (organisationId:userId).

export function identite(session: Session | null): string {
  return session?.organisationId && session.userId
    ? `${session.organisationId}:${session.userId}`
    : ''
}

/** Abonne `queryClient` aux changements de session : vide le cache quand — et SEULEMENT
 * quand — l'identité (organisation + utilisateur) change réellement. `sessionAbonnement`
 * est injectable (tests) ; en production, c'est toujours `subscribe` de `auth/session.ts`.
 * Renvoie la fonction de désabonnement (jamais utilisée en production, utile en test). */
export function armerPurgeCacheSurChangementIdentite(
  queryClient: QueryClient,
  sessionActuelle: () => Session | null = getSession,
  sessionAbonnement: (listener: (s: Session | null) => void) => (() => void) = subscribe,
): () => void {
  let derniereIdentite = identite(sessionActuelle())
  return sessionAbonnement((session) => {
    const suivante = identite(session)
    if (suivante !== derniereIdentite) {
      derniereIdentite = suivante
      queryClient.clear()
    }
  })
}
